import { useMemo, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { SearchInput } from '../../shared-components/atomic-components';
import throttle from 'lodash/throttle';
import { filterCustomers, requestCancellationToken } from '@algoan/dashboard/store/thunks';
import {
  applicationTableConfigSelector,
  filterColumnsSelector,
  filterSelector,
} from '@algoan/dashboard/store/selectors';
import { messages } from '../application/application-messages';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';

export const CustomerTableSearch: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const filterData = useSelector(filterSelector);
  const firstRender = useRef(true);
  const [input, setInput] = useState(filterData.inputValue);
  const applicationTableConfig = useSelector(applicationTableConfigSelector);
  const additionalColumns = applicationTableConfig?.additionalColumns ?? [];
  const columnFilters = useSelector(filterColumnsSelector);

  const throttledFilterApplications = useMemo(
    () =>
      throttle((input?: string) => {
        dispatch(filterCustomers({ inputValue: input?.trim() }));
      }, 500),
    [dispatch],
  );

  // This useEffect is useful when the user clicks on the reset filter button.
  // Otherwise, the input becomes out of sync with the store.
  useEffect(() => {
    setInput(filterData.inputValue);
  }, [filterData.inputValue]);

  useEffect(() => {
    // stop filtering of applications on the first render
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    // cancel previous requests for old input
    if (requestCancellationToken !== undefined) {
      requestCancellationToken.cancel('New request initiated');
    }

    throttledFilterApplications(input);
  }, [input, throttledFilterApplications]);

  const pathColumnMap: { [key: string]: string } = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'dashboard.createdAt': intl.formatMessage(messages.createdAtColumn),
    customIdentifier: intl.formatMessage(messages.referenceColumn),
    team: intl.formatMessage(messages.teamColumn),
    shares: intl.formatMessage(messages.partnersColumn),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'dashboard.currentState': intl.formatMessage(messages.statusColumn),
    numberOfAnalyses: intl.formatMessage(messages.stateColumn),
  };

  const displayPlaceholder = (): string[] => {
    const columnPlaceholders = columnFilters.map((columnPath) => {
      const additionalColumn = additionalColumns.find((column) => column.path === columnPath);
      if (additionalColumn) return additionalColumn.label;
      else {
        const path = Object.keys(pathColumnMap).find((column) => column === columnPath);
        if (path === undefined) throw new Error(`No column found for the given path ${columnPath}`);
        else return pathColumnMap[path];
      }
    });
    return columnPlaceholders.map((column) => column.toLowerCase());
  };

  return (
    <S.SearchInput
      id={'folder-id-filter-input'}
      value={input ?? ''}
      onChange={setInput}
      onCrossClick={() => setInput('')}
      placeholder={intl.formatMessage(
        {
          id: 'customer.search.placeholder',
          defaultMessage: 'Search by {columns}',
          description: 'Message for the customer search placeholder',
        },
        { columns: intl.formatList(displayPlaceholder(), { type: 'disjunction' }) },
      )}
    />
  );
};

const S = {
  SearchInput: styled(SearchInput)`
    width: 100%;
  `,
};
